import apiClient from './client';
import type {
  Organization,
  UserOrganization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  InviteUserRequest,
  OrganizationRole,
} from './types';

/**
 * List all organizations with optional filters
 * Backend: services/users/cmd/main.go (listOrganizations, lines 840-883)
 * Used for admin operations and group management
 * Returns: { "data": { "items": Organization[], "total": number } }
 */
export const listOrganizations = async (params?: {
  limit?: number;
  page?: number;
  search?: string;
  status?: string;
}): Promise<{ data: Organization[]; total: number }> => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.status) queryParams.append('status', params.status);

  const response = await apiClient.get<{ data: { items: Organization[]; total: number } }>(
    `/organizations?${queryParams.toString()}`
  );
  return {
    data: response.data.data.items,
    total: response.data.data.total,
  };
};

/**
 * Organization API Service
 * Backend: services/users/cmd/main.go (handleOrganizations function, lines 837-1178)
 * Handles all organization-related API calls for multi-tenant functionality
 */
export const organizationApi = {
  /**
   * GET /api/v1/organizations - Get all organizations the current user belongs to
   * Backend: HandleListOrganizations in pkg/handlers/organization_settings.go
   * Returns: { "success": true, "data": [{ "organization": {...}, "role": "ADMIN", "is_default": false }, ...] }
   */
  getUserOrganizations: async (): Promise<Array<{ organization: Organization; role: string; is_default: boolean }>> => {
    const response = await apiClient.get<{ data: Array<{ organization: Organization; role: string; is_default: boolean }> }>('/organizations');
    return response.data.data || [];
  },

  /**
   * GET /api/v1/organizations/:id - Get details of a specific organization by ID
   * Backend: getOrganization (lines 885-924)
   * Returns: { "data": { "organization": Organization } }
   */
  getOrganization: async (id: string): Promise<{ organization: Organization }> => {
    const response = await apiClient.get<{ data: { organization: Organization } }>(`/organizations/${id}`);
    return response.data.data;
  },

  /**
   * POST /api/v1/organizations - Create a new organization
   * Backend: createOrganization (lines 926-996)
   * User becomes member of the newly created organization
   * Returns: { "data": { "organization_id": string, "message": string } }
   */
  createOrganization: async (data: CreateOrganizationRequest): Promise<{ organization_id: string; message: string }> => {
    const response = await apiClient.post<{ data: { organization_id: string; message: string } }>('/organizations', data);
    return response.data.data;
  },

  /**
   * PUT /api/v1/organizations/:id - Update organization details
   * Backend: updateOrganization (lines 998-1094)
   * Supports: name, legal_name, tax_id, address_line1, address_line2, city, state, postal_code, country, currency, fiscal_year_end
   * Returns: { "data": { "message": "Organization updated successfully" } }
   */
  updateOrganization: async (id: string, data: UpdateOrganizationRequest): Promise<{ message: string }> => {
    const response = await apiClient.put<{ data: { message: string } }>(`/organizations/${id}`, data);
    return response.data.data;
  },

  /**
   * DELETE /api/v1/organizations/:id - Delete organization (soft delete)
   * Backend: deleteOrganization (lines 1096-1136)
   * Sets status to 'CLOSED'
   * Returns: { "data": { "message": "Organization deleted successfully" } }
   */
  deleteOrganization: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ data: { message: string } }>(`/organizations/${id}`);
    return response.data.data;
  },

  /**
   * GET /api/v1/organizations/:id/users - Get list of users in an organization
   * Backend: getOrganizationUsers (lines 1138-1169)
   * Returns: { "data": { "users": UserOrganization[], "total": number } }
   */
  getOrganizationUsers: async (organizationId: string): Promise<{ users: UserOrganization[]; total: number }> => {
    const response = await apiClient.get<{ data: { users: UserOrganization[]; total: number } }>(`/organizations/${organizationId}/users`);
    return response.data.data;
  },

  /**
   * GET /api/v1/organizations/:id/roles - Get list of roles in an organization
   * Backend: getOrganizationRoles (lines 1171-1178)
   * Note: This endpoint is a stub in the backend, may not be fully implemented yet
   */
  getOrganizationRoles: async (organizationId: string): Promise<{ roles: any[]; total: number }> => {
    const response = await apiClient.get<{ data: { roles: any[]; total: number } }>(`/organizations/${organizationId}/roles`);
    return response.data.data;
  },

  /**
   * POST /api/v1/organizations/:id/users/invite - Invite a user to join the organization
   * Backend: HandleInviteOrganizationUser in pkg/handlers/organization.go
   * Sends email invitation with link
   * Requires OWNER or ADMIN role
   */
  inviteUser: async (organizationId: string, data: InviteUserRequest): Promise<void> => {
    await apiClient.post(`/organizations/${organizationId}/users/invite`, data);
  },

  /**
   * DELETE /api/v1/organizations/:id/users/:userId - Remove a user from the organization
   * Backend: HandleRemoveOrganizationUser in pkg/handlers/organization.go
   * Requires OWNER or ADMIN role
   */
  removeUser: async (organizationId: string, userId: string): Promise<void> => {
    await apiClient.delete(`/organizations/${organizationId}/users/${userId}`);
  },

  /**
   * PUT /api/v1/organizations/:id/users/:userId/role - Update a user's role in the organization
   * Backend: HandleUpdateOrganizationUserRole in pkg/handlers/organization.go
   * Requires OWNER role
   */
  updateUserRole: async (organizationId: string, userId: string, role: OrganizationRole): Promise<void> => {
    await apiClient.put(`/organizations/${organizationId}/users/${userId}/role`, { role });
  },
};
